import { WorkflowSteps, changeSteps } from '@/components/WorkflowSteps';
import { ProjectRevisionHistory } from '@/components/ProjectRevisions';
import { QuoteReview } from '@/components/JobCostPlanning';
/**
 * CP-4.1 — Jobs: list, create wizard, detail (with versioned packages and contacts).
 *
 * Booking endpoints land in CP-4.2; this screen ends at "publish" / "cancel".
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api, apiUpload } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ClientCrewCard, BookingPoField } from "@/pages/Crews";
import {
  ClientBillableSummary,
  ClientCompletionPanel,
  ClientRateLeadForm,
} from "@/pages/Phase6";
import {
  ClientEstimatePanel,
  ClientInvoicePanel,
} from "@/pages/Phase7";
import { ClientEscrowPanel } from "@/pages/Escrow";
import { ClientPostCompletionVerifyForm } from "@/pages/Verifications";
import { budgetSummary, ClientBudgetCard, clientBudget } from "@/lib/budget";

const EDITABLE_PROJECT_STATES = ["published", "booking_requested", "accepted", "crew_assigned", "in_progress"];

export function localDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

const STATUS_TONE = {
  draft: "avcp-pill",
  published: "avcp-pill ok",
  booking_requested: "avcp-pill",
  accepted: "avcp-pill ok",
  declined: "avcp-pill warn",
  in_progress: "avcp-pill",
  completed: "avcp-pill ok",
  cancelled: "avcp-pill warn",
};

const DURATION_LABEL = {
  single_day: "Single day",
  multi_day: "Multi-day",
  multi_week: "Multi-week",
};

function StatusPill({ status }) {
  return <span className={STATUS_TONE[status] || "avcp-pill"} data-testid={TID.jobStatusBadge}>{status}</span>;
}

function DurationPill({ kind }) {
  if (!kind) return null;
  return <span className="avcp-pill" data-testid={TID.jobDurationBadge}>{DURATION_LABEL[kind] || kind}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// ---------------------------------------------------------------------------
// JOB LIST
// ---------------------------------------------------------------------------

export function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/jobs");
        setJobs(r.data || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);
  return (
    <Layout>
      <div data-testid={TID.jobsRoot}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
            <p className="text-[var(--avcp-ink-soft)] mt-1">
              Start with your project. Choose your people. Agree the work.
            </p>
          </div>
          <Link to="/app/jobs/new" className="avcp-btn primary" data-testid={TID.newJob}>+ New project</Link>
        </div>
        <WorkflowSteps current={1} />
        {loading ? <div className="avcp-card p-6">Loading…</div> : (
          <ul className="space-y-3" data-testid={TID.jobsList}>
            {jobs.length === 0 && <li className="avcp-card p-6 text-sm text-[var(--avcp-ink-soft)]">No jobs yet. Create one.</li>}
            {jobs.map((j) => (
              <li key={j.id} className="avcp-card p-5 flex flex-wrap items-center justify-between gap-4"
                  data-testid={TID.jobRow}>
                <div>
                  <Link to={`/app/jobs/${j.id}`} className="font-semibold">{j.title}</Link>
                  <div className="text-xs text-[var(--avcp-ink-soft)] mt-1 flex items-center gap-2">
                    <StatusPill status={j.status} />
                    <DurationPill kind={j.job_duration_kind} />
                    <span>{fmt(j.starts_at)} → {fmt(j.ends_at)}</span>
                    <span>· {j.currency}</span>
                    <span>· {j.helper_count_requested} helper technicians</span>
                  </div>
                  {j.budget_basis && <div className="mt-2 text-sm font-medium text-[var(--avcp-primary)]">{budgetSummary(j)}</div>}
                </div>
                <Link className="avcp-btn ghost" to={`/app/jobs/${j.id}${EDITABLE_PROJECT_STATES.includes(j.status) ? "?edit=1" : ""}`}>{EDITABLE_PROJECT_STATES.includes(j.status) ? "Edit and republish" : "Open project"}</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// JOB FORM (used by both /new and the editor on a draft job)
// ---------------------------------------------------------------------------

function ContactsEditor({ value, onChange, disabled }) {
  const setRow = (i, key, v) => {
    const next = [...value];
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { kind: "site_contact", display_name: "", phone: "", email: "", display_order: value.length + 1 }]);
  return (
    <fieldset disabled={disabled} className="space-y-3" data-testid={TID.jobContactsList}>
      {value.map((c, i) => (
        <div key={i} className="avcp-card p-4 grid md:grid-cols-5 gap-2" data-testid={`job-contact-row-${i}`}>
          <select className="avcp-input" value={c.kind} onChange={(e) => setRow(i, "kind", e.target.value)}>
            <option value="project_manager">Project Manager</option>
            <option value="site_contact">Site Contact</option>
            <option value="lead_installer">Lead Installer</option>
            <option value="service_contact">Service Contact</option>
            <option value="other">Other</option>
          </select>
          <input className="avcp-input" placeholder="Name" value={c.display_name || ""}
                 onChange={(e) => setRow(i, "display_name", e.target.value)} />
          <input className="avcp-input" placeholder="Role label" value={c.role_label || ""}
                 onChange={(e) => setRow(i, "role_label", e.target.value)} />
          <input className="avcp-input" placeholder="Phone" value={c.phone || ""}
                 onChange={(e) => setRow(i, "phone", e.target.value)} />
          <div className="flex gap-2">
            <input className="avcp-input flex-1" placeholder="Email" value={c.email || ""}
                   onChange={(e) => setRow(i, "email", e.target.value)} />
            <button className="avcp-btn ghost" onClick={() => remove(i)}
                    data-testid={TID.jobContactRemove}>×</button>
          </div>
        </div>
      ))}
      <button className="avcp-btn ghost" onClick={add} data-testid={TID.jobContactAdd}>+ Add contact</button>
    </fieldset>
  );
}

export function MultiCheckboxField({ label, help, items, value, onChange, disabled, testId }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (code) => {
    if (selected.includes(code)) {
      onChange(selected.filter((item) => item !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  return (
    <fieldset className="rounded-xl border border-[var(--avcp-line)] p-4" disabled={disabled} data-testid={testId}>
      <legend className="sr-only">{label}</legend>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-medium" aria-hidden="true">{label}</div>
          {help && <p className="mt-1 text-xs text-[var(--avcp-ink-soft)]">{help}</p>}
        </div>
        <span className="avcp-pill ink text-xs" aria-live="polite">
          {selected.length} selected
        </span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => {
          const checked = selected.includes(item.code);
          return (
            <label
              key={item.code}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                checked
                  ? "border-[var(--avcp-primary)] bg-[var(--avcp-primary-soft)]"
                  : "border-[var(--avcp-line)] bg-white hover:border-[var(--avcp-primary)]"
              } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(item.code)}
                data-testid={`${testId}-${item.code}`}
              />
              <span>{item.label}</span>
            </label>
          );
        })}
        {items.length === 0 && (
          <p className="text-sm text-[var(--avcp-ink-soft)]">No options are currently available.</p>
        )}
      </div>
    </fieldset>
  );
}

export function JobForm({ initial, onSaved, readOnly = false, catalogs, republishing = false, onCancel }) {
  const [j, setJ] = useState(() => ({
    title: "", description: "", template_code: "", region_code: "",
    starts_at: "", ends_at: "",
    service_address: "", service_city: "", service_postal: "",
    helper_count_requested: 0, currency: "USD",
    budget_basis: "fixed", budget_amount: "", budget_units: "",
    required_skills_json: [], language_codes_json: [],
    notes_internal: "",
    meet_location: "", meet_at: "", parking_instructions: "", site_access_notes: "",
    contacts: [], ...initial,
  }));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const set = (k, v) => setJ((p) => ({ ...p, [k]: v }));
  const budgetBasis = j.budget_basis || "fixed";
  const save = async () => {
    if (saving) return;
    setSaving(true); setSaveError("");
    try {
      if (republishing && !reason.trim()) throw new Error("Explain what changed for the team.");
      const allowedFields = [
        "title", "description", "template_code", "region_code",
        "service_address", "service_city", "service_postal", "service_lat", "service_lon",
        "starts_at", "ends_at", "helper_count_requested", "currency",
        "budget_basis", "budget_amount", "budget_units",
        "required_skills_json", "required_certifications_json", "language_codes_json",
        "notes_internal", "meet_location", "meet_at", "parking_instructions", "site_access_notes",
        "contacts",
      ];
      const body = Object.fromEntries(
        allowedFields.filter((key) => Object.prototype.hasOwnProperty.call(j, key)).map((key) => [key, j[key]])
      );
      if (body.contacts) body.contacts = body.contacts.map(({id, ...contact}) => contact);
      // Preserve the original timestamp, including seconds, when a date field was not edited.
      ["starts_at", "ends_at", "meet_at"].forEach(k => {
        if (body[k] === initial?.[k] && initial?.original_dates && k in initial.original_dates) body[k] = initial.original_dates[k];
      });
      // datetime-local strings → ISO; empty optionals → null (Pydantic rejects "" for datetime/number).
      ["starts_at", "ends_at", "meet_at"].forEach((k) => {
        if (body[k] && typeof body[k] === "string" && body[k].length === 16) {
          body[k] = new Date(body[k]).toISOString();
        } else if (body[k] === "") {
          body[k] = null;
        }
      });
      // Strip empty-string optional fields so Pydantic doesn't fail on type=str|None
      Object.keys(body).forEach((k) => {
        if (body[k] === "") body[k] = null;
      });
      // Coerce numeric
      if (body.helper_count_requested != null && body.helper_count_requested !== "") {
        body.helper_count_requested = Number(body.helper_count_requested);
      }
      body.currency = String(body.currency || "").toUpperCase();
      if (body.budget_amount == null) {
        body.budget_basis = null;
        body.budget_amount = null;
        body.budget_units = null;
      } else {
        body.budget_basis = body.budget_basis || "fixed";
        body.budget_amount = Number(body.budget_amount);
        if (!Number.isFinite(body.budget_amount) || body.budget_amount <= 0) {
          throw new Error("Enter a client budget greater than zero.");
        }
        if (body.budget_basis === "fixed") {
          body.budget_units = null;
        } else {
          body.budget_units = Number(body.budget_units);
          if (!Number.isFinite(body.budget_units) || body.budget_units <= 0) {
            throw new Error(`Enter the planned ${body.budget_basis === "hourly" ? "hours" : "days"}.`);
          }
        }
      }
      const r = await onSaved(body, reason.trim());
      if (!republishing) toast.success("Saved");
      return r;
    } catch (e) {
      const detail = e?.problem?.detail || e?.problem?.title || e.message || "Save failed";
      setSaveError(detail); toast.error(detail);
    } finally { setSaving(false); }
  };
  const disabled = readOnly || saving;
  return (
    <div className="space-y-6">
      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Basics</h2>
        <input aria-label="Project title" className="avcp-input" data-testid={TID.jobTitle} placeholder="Project title"
               value={j.title} disabled={disabled}
               onChange={(e) => set("title", e.target.value)} />
        <textarea aria-label="Scope of work" className="avcp-input" data-testid={TID.jobDescription} rows="4" placeholder="Description"
                  value={j.description || ""} disabled={disabled}
                  onChange={(e) => set("description", e.target.value)} />
        <div className="grid md:grid-cols-2 gap-3">
          <select className="avcp-input" data-testid={TID.jobTemplate}
                  value={j.template_code || ""} disabled={disabled}
                  onChange={(e) => set("template_code", e.target.value)}>
            <option value="">No template</option>
            {catalogs.templates.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          <select className="avcp-input" aria-label="Service region" data-testid={TID.jobRegion}
                  value={j.region_code || ""} disabled={disabled}
                  onChange={(e) => set("region_code", e.target.value)}>
            <option value="">Select region…</option>
            {catalogs.regions.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>
      </section>

      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Schedule</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <input type="datetime-local" className="avcp-input" aria-label="Project start" data-testid={TID.jobStartsAt}
                 value={j.starts_at?.slice ? j.starts_at.slice(0, 16) : ""}
                 disabled={disabled}
                 onChange={(e) => set("starts_at", e.target.value)} />
          <input type="datetime-local" className="avcp-input" aria-label="Project end" data-testid={TID.jobEndsAt}
                 value={j.ends_at?.slice ? j.ends_at.slice(0, 16) : ""}
                 disabled={disabled}
                 onChange={(e) => set("ends_at", e.target.value)} />
        </div>
        <p className="text-xs text-[var(--avcp-ink-soft)]">
          Choose the start and end of the project. Working hours are agreed separately with your contractor.
        </p>
      </section>

      <section className="avcp-card p-6 space-y-4" data-testid="job-client-budget">
        <div>
          <h2 className="font-semibold">Client target budget</h2>
          <p className="mt-1 text-xs text-[var(--avcp-ink-soft)]">
            Tell contractors what budget you have in mind so they can respond quickly. This is non-binding; approved time and accepted rates control final billing.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <label className="grid gap-1 text-sm">
            <span>Budget type</span>
            <select className="avcp-input" value={budgetBasis} disabled={disabled}
              data-testid="job-budget-basis"
              onChange={(e) => setJ((current) => ({ ...current, budget_basis: e.target.value, budget_units: e.target.value === "fixed" ? "" : current.budget_units }))}>
              <option value="fixed">Fixed project total</option>
              <option value="hourly">Hourly budget</option>
              <option value="daily">Daily budget</option>
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>{budgetBasis === "fixed" ? "Total budget" : budgetBasis === "daily" ? "Budget per day" : "Budget per hour"}</span>
            <input className="avcp-input" type="number" min="0.01" step="0.01" value={j.budget_amount ?? ""} disabled={disabled}
              data-testid="job-budget-amount" placeholder="0.00" onChange={(e) => set("budget_amount", e.target.value)} />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Currency</span>
            <input className="avcp-input" maxLength="3" list="project-currency-options"
              data-testid={TID.jobCurrency} value={j.currency || ""} disabled={disabled}
              onChange={(e) => set("currency", e.target.value.toUpperCase())} placeholder="CAD" />
            <datalist id="project-currency-options">
              {["CAD", "USD", "EUR", "GBP", "AUD"].map((code) => <option key={code} value={code} />)}
            </datalist>
          </label>
          {budgetBasis !== "fixed" && (
            <label className="grid gap-1 text-sm">
              <span>Planned {budgetBasis === "daily" ? "days" : "hours"}</span>
              <input className="avcp-input" type="number" min="0.25" step="0.25" value={j.budget_units ?? ""} disabled={disabled}
                data-testid="job-budget-units" placeholder={budgetBasis === "daily" ? "Days" : "Hours"}
                onChange={(e) => set("budget_units", e.target.value)} />
            </label>
          )}
        </div>
        {clientBudget(j) && <ClientBudgetCard record={j} compact />}
        {!readOnly && !clientBudget(j) && <p className="text-xs text-[var(--avcp-ink-soft)]">A complete budget is required before a new project can be published.</p>}
      </section>

      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Crew requirements</h2>
        <label className="grid max-w-md gap-1 text-sm">
          <span>Helper technicians requested</span>
          <input className="avcp-input" type="number" min="0" max="20"
                 data-testid={TID.jobHelperCount}
                 value={j.helper_count_requested || 0} disabled={disabled}
                 onChange={(e) => set("helper_count_requested", Number(e.target.value))}
                 placeholder="Number of helper technicians" />
          <span className="text-xs text-[var(--avcp-ink-soft)]">This is a headcount, not a price.</span>
        </label>
      </section>

      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Requirements</h2>
        <MultiCheckboxField
          label="Required workloads"
          help="Select every type of work needed for this project. No keyboard modifier is required."
          items={catalogs.skills}
          value={j.required_skills_json}
          disabled={disabled}
          testId={TID.jobSkills}
          onChange={(value) => set("required_skills_json", value)}
        />
        <MultiCheckboxField
          label="Preferred languages"
          help="Select all languages that apply."
          items={catalogs.languages}
          value={j.language_codes_json}
          disabled={disabled}
          testId={TID.jobLanguages}
          onChange={(value) => set("language_codes_json", value)}
        />
        <textarea className="avcp-input" rows="2" data-testid={TID.jobNotesInternal}
                  placeholder="Internal notes (private to client + lead post-booking)"
                  value={j.notes_internal || ""} disabled={disabled}
                  onChange={(e) => set("notes_internal", e.target.value)} />
      </section>

      <details className="avcp-card p-5"><summary className="cursor-pointer font-semibold">Site details and contacts</summary><p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">Add the address, meeting point, site access and people to contact.</p><div className="mt-4 space-y-4">
      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Location</h2>
        <input className="avcp-input" data-testid={TID.jobAddress} placeholder="Street address (private)"
               value={j.service_address || ""} disabled={disabled}
               onChange={(e) => set("service_address", e.target.value)} />
        <div className="grid md:grid-cols-2 gap-3">
          <input className="avcp-input" data-testid={TID.jobCity} placeholder="City (visible pre-booking)"
                 value={j.service_city || ""} disabled={disabled}
                 onChange={(e) => set("service_city", e.target.value)} />
          <input className="avcp-input" data-testid={TID.jobPostal} placeholder="Postal (private)"
                 value={j.service_postal || ""} disabled={disabled}
                 onChange={(e) => set("service_postal", e.target.value)} />
        </div>
      </section>

      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Meeting & site access</h2>
        <input className="avcp-input" data-testid={TID.jobMeetLocation} placeholder="Meet location"
               value={j.meet_location || ""} disabled={disabled}
               onChange={(e) => set("meet_location", e.target.value)} />
        <input type="datetime-local" className="avcp-input" data-testid={TID.jobMeetAt}
               value={j.meet_at?.slice ? j.meet_at.slice(0, 16) : ""}
               disabled={disabled}
               onChange={(e) => set("meet_at", e.target.value)} />
        <textarea className="avcp-input" rows="2" data-testid={TID.jobParking} placeholder="Parking instructions"
                  value={j.parking_instructions || ""} disabled={disabled}
                  onChange={(e) => set("parking_instructions", e.target.value)} />
        <textarea className="avcp-input" rows="2" data-testid={TID.jobAccessNotes} placeholder="Site access notes"
                  value={j.site_access_notes || ""} disabled={disabled}
                  onChange={(e) => set("site_access_notes", e.target.value)} />
      </section>

      <section className="avcp-card p-6 space-y-3">
        <h2 className="font-semibold">Contacts</h2>
        <ContactsEditor disabled={disabled} value={j.contacts || []} onChange={(v) => set("contacts", v)} />
      </section>
      </div></details>

      {!readOnly && (
        <section className="avcp-card p-6 space-y-3">
          {republishing && <>
            <h2 className="font-semibold">Republish your changes</h2>
            <p className="text-sm">Everyone involved will be notified. Changes to agreed dates, scope, crew or budget need contractor reconfirmation and your quote approval. The current agreement stays in place until then.</p>
            <label className="grid gap-1 text-sm">What changed and why?<textarea className="avcp-input" rows="3" maxLength="2000" value={reason} disabled={saving} onChange={e => setReason(e.target.value)} placeholder="For example: the site will be ready a day later, and we need another technician." /></label>
          </>}
          {saveError && <p role="alert" className="text-rose-700">{saveError}</p>}
          <div className="flex flex-wrap gap-3">
            <button className="avcp-btn primary" disabled={saving || (republishing && !reason.trim())} data-testid={TID.jobSave} onClick={save}>{saving ? "Saving…" : republishing ? "Republish and notify everyone" : "Save draft"}</button>
            {onCancel && <button className="avcp-btn ghost" disabled={saving} onClick={onCancel}>Discard these edits</button>}
          </div>
        </section>
      )}
    </div>
  );
}

async function loadCatalogs() {
  const [t, r, s, l] = await Promise.all([
    api("/templates"), api("/catalog/regions"), api("/catalog/skills"), api("/catalog/languages"),
  ]);
  return { templates: t.data || [], regions: r.data || [], skills: s.data || [], languages: l.data || [] };
}

export function NewJob() {
  const navigate = useNavigate();
  const [catalogs, setCatalogs] = useState({ templates: [], regions: [], skills: [], languages: [] });
  useEffect(() => { loadCatalogs().then(setCatalogs); }, []);
  return (
    <Layout>
      <h1 className="text-3xl font-bold tracking-tight mb-6">New project</h1>
      <WorkflowSteps current={1} />
      <p className="mb-5 text-sm text-[var(--avcp-ink-soft)]">Enter the work, dates, crew and budget. Save your draft, then publish when ready.</p>
      <JobForm initial={{}} catalogs={catalogs} onSaved={async (body) => {
        const r = await api("/jobs", { method: "POST", body });
        navigate(`/app/jobs/${r.id}`);
        return r;
      }} />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// JOB DETAIL
// ---------------------------------------------------------------------------

function PackageUploader({ jobId, packageId = null, kind = "attachment", title = "", onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [k, setK] = useState(kind);
  const [t, setT] = useState(title);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const submit = async () => {
    if (!file) { toast.error("Select a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", k);
      fd.append("title", t);
      if (note) fd.append("upload_note", note);
      if (packageId) fd.append("package_id", packageId);
      // We reuse fetch directly since apiUpload only sends a single file field
      const { BASE: _ignore } = await import("@/lib/api"); // ensures module is loaded; we use fetch directly here
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/jobs/${jobId}/packages`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("avcp_access_token") || ""}` },
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.title || data.detail || "Upload failed");
      toast.success(packageId ? `Version ${data.version} added` : `Package created (v${data.version})`);
      setFile(null); setNote("");
      onUploaded(data);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-5 gap-2 items-end" data-testid={packageId ? TID.pkgAddVersion : TID.pkgUpload}>
      {!packageId && (
        <>
          <select className="avcp-input" data-testid={TID.pkgUploadKind} value={k} onChange={(e) => setK(e.target.value)}>
            <option value="attachment">Attachment</option>
            <option value="sow">SOW</option>
            <option value="floorplan">Floor plan</option>
            <option value="equipment_list">Equipment list</option>
          </select>
          <input className="avcp-input md:col-span-2" data-testid={TID.pkgUploadTitle}
                 placeholder="Package title" value={t} onChange={(e) => setT(e.target.value)} />
        </>
      )}
      <input className="avcp-input md:col-span-2" data-testid={TID.pkgUploadNote}
             placeholder={packageId ? "What changed?" : "Optional note"}
             value={note} onChange={(e) => setNote(e.target.value)} />
      <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <button className="avcp-btn primary" data-testid={TID.pkgUploadSubmit}
              disabled={busy || !file || (!packageId && !t)} onClick={submit}>
        {busy ? "Uploading…" : (packageId ? "Add version" : "Create package")}
      </button>
    </div>
  );
}

export function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(() => new URLSearchParams(window.location.search).get("edit") === "1");
  const [catalogs, setCatalogs] = useState({ templates: [], regions: [], skills: [], languages: [] });
  const refresh = async (force = false) => {
    if (editing && job && !force) return;
    try {
      const r = await api(`/jobs/${id}`);
      setJob(r);
      try {
        const bks = await api(`/jobs/${id}/bookings`);
        setBookings(bks.data || []);
      } catch { /* booking list is optional pre-request */ }
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { refresh(); loadCatalogs().then(setCatalogs); }, [id]);
  useEffect(() => {
    if (editing || !EDITABLE_PROJECT_STATES.includes(job?.status)) return;
    let active = true, inFlight = false;
    const timer = setInterval(async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const [updatedJob, updatedBookings] = await Promise.all([api(`/jobs/${id}`), api(`/jobs/${id}/bookings`)]);
        if (active) { setJob(updatedJob); setBookings(updatedBookings.data || []); }
      } catch { /* A temporary connection failure keeps the last known state. */ }
      finally { inFlight = false; }
    }, 10000);
    return () => { active = false; clearInterval(timer); };
  }, [id, job?.status, editing]);

  const initial = useMemo(() => {
    if (!job) return {};
    const source = { ...job, ...(editing ? job.pending_revision?.proposed : {}) };
    return {
      ...source,
      original_dates: Object.fromEntries(["starts_at", "ends_at", "meet_at"].map(k => [k, source[k]])),
      region_code: catalogs.regions.find(r => r.id === source.region_id)?.code || "",
      ...Object.fromEntries(["starts_at", "ends_at", "meet_at"].map(k => [k, localDateInput(source[k])])),
    };
  }, [job, catalogs, editing]);

  if (!job) return <Layout><div className="avcp-card p-6">Loading…</div></Layout>;

  const isDraft = job.status === "draft";
  const canEdit = EDITABLE_PROJECT_STATES.includes(job.status);
  const canPublish = job.status === "draft";
  const canCancel = ["draft", "published", "booking_requested", "accepted"].includes(job.status);

  const publish = async () => {
    try {
      await api(`/jobs/${id}/publish`, { method: "POST" });
      toast.success("Published");
      await refresh();
    } catch (e) { toast.error(e.message); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this project? Everyone involved should agree how to handle any work already done.")) return;
    try {
      await api(`/jobs/${id}/cancel`, { method: "POST" });
      toast.success("Cancelled");
      await refresh();
    } catch (e) { toast.error(e.message); }
  };

  // Initial values for the form (transform datetime back to local-input shape)
  return (
    <Layout>
      <div data-testid={TID.jobDetailRoot}>
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{job.title}</h1>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <StatusPill status={job.status} />
              <DurationPill kind={job.job_duration_kind} />
              <span className="text-[var(--avcp-ink-soft)]">{fmt(job.starts_at)} → {fmt(job.ends_at)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {job.status === 'published' && <Link className="avcp-btn primary" to={`/app/find-contractors?job=${id}`}>Find contractor & send job request</Link>}
            {canEdit && !editing && <button className="avcp-btn primary" onClick={() => setEditing(true)}>Edit and republish</button>}
            {canPublish && (
              <button className="avcp-btn primary" data-testid={TID.jobPublish} onClick={publish}>Publish</button>
            )}
            {canCancel && !editing && (
              <button className="avcp-btn ghost" data-testid={TID.jobCancel} onClick={cancel}>Cancel</button>
            )}
          </div>
        </div>

        <WorkflowSteps current={editing ? 1 : job.pending_revision?.state === "awaiting_client" ? 3 : job.pending_revision ? 2 : isDraft ? 1 : job.status === "published" ? 2 : 3} steps={editing || job.pending_revision ? changeSteps : undefined} />
        {!editing && <div className="rounded-2xl bg-teal-50 border border-teal-200 p-5 mb-6"><h2 className="font-semibold">Your next step</h2><p className="mt-1 text-sm">{job.pending_revision ? (job.pending_revision.state === "awaiting_client" ? "Review the contractor’s quote below. Approve it to put the project changes into effect, or request another revision." : "The contractor is reviewing your changes. You can edit them again or withdraw them below.") : isDraft ? "Check the project details, save any edits, then choose Publish." : job.status === "published" ? "Choose Find contractor above. Start with your preferred people and compare the available crews." : job.status === "booking_requested" ? "The contractor will review the job and send a quote. Review and approve it here to confirm the booking." : ["accepted","crew_assigned","in_progress"].includes(job.status) ? "Your booking is confirmed. Review the crew and work below. If plans change, choose Edit and republish." : "This project is closed. You can review its details and history below."}</p></div>}
        {editing && canEdit && <div className="avcp-card p-5 mb-6 border border-teal-500"><strong>{job.pending_revision ? "Editing the proposed revision" : "Editing your published project"}</strong><p className="mt-1 text-sm">Update the details below, then explain the changes and republish. You can revise again if plans change.</p></div>}
        {!isDraft && !editing && <ProjectRevisionHistory jobId={id} refreshKey={job.updated_at} onChange={refresh} />}
        {!editing && bookings.filter(bk => ['requested','accepted','crew_assigned'].includes(bk.state)).map(bk => <div key={`quote-${bk.id}-${job.pending_revision?.id || "current"}`} className="mb-6">
          <p className="text-sm font-medium">Job request to {bk.contractor_company || bk.lead_company || 'your contractor'}</p>
          <QuoteReview bookingId={bk.id} onChange={refresh} />
        </div>)}

        {isDraft || (canEdit && editing) ? (
          <JobForm key={`${id}-${isDraft ? "draft" : "editing"}-${catalogs.regions.length}`} initial={initial} catalogs={catalogs} republishing={!isDraft} onCancel={!isDraft ? () => setEditing(false) : undefined}
                   onSaved={async (body, reason) => {
                     const r = isDraft ? await api(`/jobs/${id}`, { method: "PATCH", body }) : await api(`/jobs/${id}/republish`, { method: "POST", body: { changes: body, expected_updated_at: job.updated_at, reason } });
                     if (!isDraft) { setEditing(false); toast.success(r.review_required ? "Republished — waiting for contractor reconfirmation" : r.unchanged ? "No changes to republish" : "Project updated and team notified"); }
                     await refresh(true);
                     return r;
                   }} />
        ) : (
          <details className="avcp-card p-5"><summary className="cursor-pointer font-semibold">Current project details</summary><div className="mt-5"><JobForm key={`${job.updated_at}-${catalogs.regions.length}`} initial={initial} catalogs={catalogs} readOnly onSaved={() => {}} /></div></details>
        )}

        {!editing && (isDraft || job.packages?.length > 0) && <section className="avcp-card p-6 mt-6">
          <h2 className="font-semibold mb-3">Project documents</h2>
          {isDraft && (
            <div className="mb-4">
              <PackageUploader jobId={id} onUploaded={refresh} />
            </div>
          )}
          <ul className="space-y-3" data-testid={TID.pkgList}>
            {(job.packages || []).length === 0 && (
              <li className="text-sm text-[var(--avcp-ink-soft)]">No packages yet.</li>
            )}
            {(job.packages || []).map((p) => (
              <li key={p.id} className="border border-[var(--avcp-line)] rounded p-4" data-testid={TID.pkgRow}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-[var(--avcp-ink-soft)]">{p.kind} · visibility {p.visibility}</div>
                  </div>
                  <span className="avcp-pill">{p.versions.length} versions</span>
                </div>
                <ul className="text-sm space-y-1">
                  {p.versions.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-[var(--avcp-bg-2)]"
                        data-testid={TID.pkgVersionRow}>
                      <span className="font-mono text-xs">v{v.version}</span>
                      <span className="flex-1 text-xs text-[var(--avcp-ink-soft)] truncate">
                        {fmt(v.created_at)} · {v.mime_type} · {(v.size_bytes / 1024).toFixed(1)} KB
                        {v.upload_note ? ` · "${v.upload_note}"` : ""}
                      </span>
                      <a className="avcp-btn ghost" href={v.download_url} target="_blank" rel="noreferrer">Download</a>
                    </li>
                  ))}
                </ul>
                {isDraft && (
                  <div className="mt-3">
                    <PackageUploader jobId={id} packageId={p.id} onUploaded={refresh} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>}

        {!editing && bookings.length > 0 && (
          <details className="avcp-card p-5 mt-6">
            <summary className="cursor-pointer font-semibold mb-3">Booking, crew and billing</summary>
            <div className="space-y-4">
              {bookings.map((bk) => (
                <div key={bk.id} className="space-y-3">
                  <div className="avcp-card p-4 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="avcp-pill">{bk.state}</span>
                      <span>Contractor: {bk.contractor_company || bk.lead_company || bk.lead_user_uuid?.slice(0, 8)}</span>
                      <span>· {bk.helper_count_requested} helper technicians · {bk.currency}</span>
                    </div>
                    <div className="mt-3"><ClientBudgetCard record={bk} compact /></div>
                  </div>
                  {bk.client_po_number && (
                    <BookingPoField
                      jobId={id}
                      booking={bk}
                      onUpdate={(updated) => setBookings((prev) => prev.map((x) => x.id === updated.id ? { ...x, ...updated } : x))}
                    />
                  )}
                  {bk.state === 'requested' && <p className="text-sm">Job request sent. Review the contractor’s quote above when it arrives, then approve it to confirm the job.</p>}
                  {bk.lead_user_id && <ClientCrewCard jobId={id} bookingId={bk.id} />}
                  {/* CP-7.3 — Estimate panel (post-acceptance) */}
                  <ClientEstimatePanel jobId={id} bookingId={bk.id} bookingState={bk.state} />
                  {/* CP-6.3 — Phase 6 surfaces */}
                  <ClientCompletionPanel
                    jobId={id}
                    bookingId={bk.id}
                    bookingState={bk.state}
                    onChange={refresh}
                  />
                  <ClientBillableSummary
                    jobId={id}
                    bookingId={bk.id}
                    bookingState={bk.state}
                  />
                  <ClientRateLeadForm
                    jobId={id}
                    bookingId={bk.id}
                    bookingState={bk.state}
                  />
                  {/* CP-7.3 — Invoice + variance pill (post-completion) */}
                  <ClientInvoicePanel jobId={id} bookingId={bk.id} bookingState={bk.state} />
                  {/* CP-11.2 M3 — Escrow status + release timeline (read-only) */}
                  <ClientEscrowPanel jobId={id} bookingId={bk.id} />
                  {/* CP-12.5 — Client post-completion verification */}
                  <ClientPostCompletionVerifyForm
                    jobId={id}
                    bookingId={bk.id}
                    bookingState={bk.state}
                    leadUserId={bk.lead_user_id}
                  />
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </Layout>
  );
}
